/**
 * Velour — Insta Video Vitrini
 * - Autoplays (muted) the tiles that are on screen, pauses the rest.
 * - Sound button unmutes one tile at a time.
 * - Arrow buttons scroll the snap track by one tile.
 * Quick-view is handled globally by vl-quick-view.js via [data-vl-quick-view].
 */
(function () {
  if (customElements.get('vl-insta-video')) return;

  class VlInstaVideo extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-vl-ig-track]');
      this.videos = Array.from(this.querySelectorAll('[data-vl-ig-video]'));
      this.prev = this.querySelector('[data-vl-ig-prev]');
      this.next = this.querySelector('[data-vl-ig-next]');
      this.autoplay = this.getAttribute('data-autoplay') !== 'false';
      this.unmuted = null;

      this.onScroll = this.onScroll.bind(this);
      this.updateArrows = this.updateArrows.bind(this);

      if (this.prev) this.prev.addEventListener('click', () => this.scrollByItem(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByItem(1));
      if (this.track) {
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.updateArrows, { passive: true });
      }

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-vl-ig-sound]');
        if (!btn) return;
        e.preventDefault();
        this.toggleSound(btn);
      });

      this.setupAutoplay();
      this.updateArrows();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      window.removeEventListener('resize', this.updateArrows);
    }

    setupAutoplay() {
      if (!this.videos.length) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.autoplay || reduced || !('IntersectionObserver' in window)) return;

      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          const item = v.closest('.vl-ig__item');
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
            if (item) item.classList.add('vl-ig__item--playing');
          } else {
            v.pause();
            if (item) item.classList.remove('vl-ig__item--playing');
            if (this.unmuted === v) this.muteAll();
          }
        });
      }, { threshold: [0, 0.5, 1] });

      this.videos.forEach((v) => this.io.observe(v));
    }

    toggleSound(btn) {
      const item = btn.closest('.vl-ig__item');
      const v = item && item.querySelector('[data-vl-ig-video]');
      if (!v) return;
      const willUnmute = v.muted;
      this.muteAll();
      if (willUnmute) {
        v.muted = false;
        v.volume = 1;
        this.unmuted = v;
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Sesi kapat');
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    }

    muteAll() {
      this.videos.forEach((v) => { v.muted = true; });
      this.querySelectorAll('[data-vl-ig-sound]').forEach((b) => {
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-label', 'Sesi aç');
      });
      this.unmuted = null;
    }

    itemWidth() {
      const first = this.track && this.track.querySelector('.vl-ig__item');
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    scrollByItem(dir) {
      if (!this.track) return;
      const w = this.itemWidth();
      this.track.scrollBy({ left: dir * w, behavior: 'smooth' });
    }

    onScroll() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateArrows();
      });
    }

    updateArrows() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.toggleAttribute('disabled', this.track.scrollLeft <= 2);
      if (this.next) this.next.toggleAttribute('disabled', this.track.scrollLeft >= max);
    }
  }

  customElements.define('vl-insta-video', VlInstaVideo);

  // Theme editor: re-init when the section is (re)loaded.
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target && e.target.querySelector && e.target.querySelector('vl-insta-video');
    if (el && el.updateArrows) el.updateArrows();
  });
})();
